/**
 * QA Hub Navigation Configuration
 * 
 * PATH: apps/qa/src/config/navigation.ts
 * PURPOSE: Define navigation items and module config for QA Hub
 * 
 * This follows the unified module pattern from NAVIGATION_SPEC.md:
 * - Dashboard always first
 * - CORE WORK: Hub-specific primary entities (Inspections, NCRs, Templates, Evidence)
 * - TOOLS: Reports, Sign-offs, AI features, collaboration tools
 * - QUICK ACCESS: User-customizable cross-hub shortcuts
 * - Settings: Always last
 * 
 * Uses @quotech/ui for unified navigation across all Quotech hubs.
 */

import {
  LayoutDashboard,
  ClipboardCheck,
  FileText,
  Settings,
  AlertTriangle,
  Camera,
  CheckCircle,
  BarChart,
  Briefcase,
  FileEdit,
  Mail,
  Upload,
  Plus,
  Sparkles,
  History,
  MessageSquare,
  Users,
  FolderSearch,
} from 'lucide-react'
import type { NavItem, ExternalLink, CommandItem, ModuleConfig, HubId, QuickAddItem } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// =============================================================================
// MODULE CONFIG
// =============================================================================

export const MODULE_CONFIG: ModuleConfig = {
  id: 'qa' as HubId,
  name: 'QA Hub',
  icon: ClipboardCheck,
  accentColor: 'green',
  accentGradient: 'from-green-500 to-emerald-600',
  accentBgClass: 'bg-green-600',
  accentHoverClass: 'hover:bg-green-700',
  accentTextClass: 'text-green-600',
  accentBadgeBgClass: 'bg-green-100',
  accentBadgeTextClass: 'text-green-700',
  baseUrl: 'https://qa.quotech.ai',
}

// =============================================================================
// SIDEBAR SECTION LABELS
// Following NAVIGATION_SPEC.md standard:
// - CORE WORK (not "MAIN")
// - TOOLS
// - QUICK ACCESS (user-customizable)
// - HUBS
// =============================================================================

export const sidebarSectionLabels = {
  main: 'CORE WORK',        // Per NAVIGATION_SPEC.md - hub-specific primary items
  secondary: 'TOOLS',       // Reports, templates, utilities
  quickAccess: 'QUICK ACCESS',
  hubs: 'HUBS',
}

// =============================================================================
// MAIN NAVIGATION (Core Work)
// Per NAVIGATION_SPEC.md QA Hub spec:
// Dashboard, Inspections, NCRs/Defects, Templates, Evidence
// =============================================================================

export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
  },
  {
    id: 'inspections',
    label: 'Inspections',
    icon: ClipboardCheck,
    href: '/records',
  },
  {
    id: 'ncrs',
    label: 'NCRs/Defects',
    icon: AlertTriangle,
    href: '/ncrs',
    badge: 0, // Dynamic - will be updated from API
    badgeVariant: 'warning',
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: FileText,
    href: '/templates',
  },
  {
    id: 'evidence',
    label: 'Evidence',
    icon: Camera,
    href: '/evidence',
  },
]

// =============================================================================
// SECONDARY NAVIGATION (Tools)
// Per NAVIGATION_SPEC.md: Reports, Sign-offs
// Extended with: AI Insights, Team Chat, Audit Log (premium features)
// =============================================================================

export const SECONDARY_NAV_ITEMS: NavItem[] = [
  {
    id: 'reports',
    label: 'Reports',
    icon: BarChart,
    href: '/reports',
  },
  {
    id: 'signoffs',
    label: 'Sign-offs',
    icon: CheckCircle,
    href: '/signoffs',
  },
  {
    id: 'ai-insights',
    label: 'AI Insights',
    icon: Sparkles,
    href: '/ai',
    badge: 'AI',
    badgeVariant: 'info',
  },
  {
    id: 'chat',
    label: 'Team Chat',
    icon: MessageSquare,
    href: '/chat',
  },
  {
    id: 'audit',
    label: 'Audit Log',
    icon: History,
    href: '/audit',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '/settings',
  },
]

// =============================================================================
// EXTERNAL HUB LINKS
// =============================================================================

export const EXTERNAL_LINKS: ExternalLink[] = getExternalLinks('qa')

// =============================================================================
// QUICK ADD ITEMS (Header dropdown)
// =============================================================================

export const QUICK_ADD_ITEMS: QuickAddItem[] = [
  {
    id: 'new-inspection',
    label: 'New Inspection',
    icon: ClipboardCheck,
    action: () => { window.location.href = '/records/new' },
    shortcut: '⌘⇧I',
  },
  {
    id: 'new-ncr',
    label: 'New NCR',
    icon: AlertTriangle,
    action: () => { window.location.href = '/ncrs/new' },
    shortcut: '⌘⇧N',
  },
  {
    id: 'upload-evidence',
    label: 'Upload Evidence',
    icon: Upload,
    action: () => { window.location.href = '/evidence/upload' },
    shortcut: '⌘⇧E',
  },
]

// =============================================================================
// COMMAND PALETTE COMMANDS
// =============================================================================

export const COMMANDS: CommandItem[] = [
  // Actions
  {
    id: 'new-inspection',
    label: 'New Inspection',
    description: 'Create a new QA inspection record',
    icon: Plus,
    action: () => { window.location.href = '/records/new' },
    keywords: ['create', 'add', 'inspection', 'record'],
    category: 'action',
  },
  {
    id: 'new-ncr',
    label: 'New NCR',
    description: 'Create a non-conformance record',
    icon: AlertTriangle,
    action: () => { window.location.href = '/ncrs/new' },
    keywords: ['create', 'add', 'ncr', 'non-conformance', 'defect'],
    category: 'action',
  },
  {
    id: 'upload-evidence',
    label: 'Upload Evidence',
    description: 'Upload photos or documents as evidence',
    icon: Upload,
    action: () => { window.location.href = '/evidence/upload' },
    keywords: ['upload', 'photo', 'evidence', 'document'],
    category: 'action',
  },
  
  // Navigation
  {
    id: 'go-dashboard',
    label: 'Go to Dashboard',
    icon: LayoutDashboard,
    action: () => { window.location.href = '/' },
    keywords: ['navigate', 'home', 'dashboard'],
    category: 'navigation',
  },
  {
    id: 'go-inspections',
    label: 'Go to Inspections',
    icon: ClipboardCheck,
    action: () => { window.location.href = '/records' },
    keywords: ['navigate', 'inspections', 'records'],
    category: 'navigation',
  },
  {
    id: 'go-ncrs',
    label: 'Go to NCRs',
    icon: AlertTriangle,
    action: () => { window.location.href = '/ncrs' },
    keywords: ['navigate', 'ncrs', 'defects', 'non-conformance'],
    category: 'navigation',
  },
  {
    id: 'go-templates',
    label: 'Go to Templates',
    icon: FileText,
    action: () => { window.location.href = '/templates' },
    keywords: ['navigate', 'templates', 'checklists'],
    category: 'navigation',
  },
  {
    id: 'go-evidence',
    label: 'Go to Evidence',
    icon: Camera,
    action: () => { window.location.href = '/evidence' },
    keywords: ['navigate', 'evidence', 'photos', 'documents'],
    category: 'navigation',
  },
  {
    id: 'go-reports',
    label: 'Go to Reports',
    icon: BarChart,
    action: () => { window.location.href = '/reports' },
    keywords: ['navigate', 'reports', 'analytics'],
    category: 'navigation',
  },
  {
    id: 'go-signoffs',
    label: 'Go to Sign-offs',
    icon: CheckCircle,
    action: () => { window.location.href = '/signoffs' },
    keywords: ['navigate', 'signoffs', 'approvals', 'hold points'],
    category: 'navigation',
  },
  {
    id: 'go-ai-insights',
    label: 'Go to AI Insights',
    icon: Sparkles,
    action: () => { window.location.href = '/ai' },
    keywords: ['navigate', 'ai', 'insights', 'analysis'],
    category: 'navigation',
  },
  {
    id: 'go-chat',
    label: 'Go to Team Chat',
    icon: MessageSquare,
    action: () => { window.location.href = '/chat' },
    keywords: ['navigate', 'chat', 'team', 'collaboration'],
    category: 'navigation',
  },
  {
    id: 'go-audit',
    label: 'Go to Audit Log',
    icon: History,
    action: () => { window.location.href = '/audit' },
    keywords: ['navigate', 'audit', 'log', 'history'],
    category: 'navigation',
  },
  
  // Hub switching
  {
    id: 'switch-jobs',
    label: 'Switch to Jobs Hub',
    icon: Briefcase,
    action: () => { window.location.href = 'https://dashboard.quotech.ai' },
    keywords: ['hub', 'jobs', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-crm',
    label: 'Switch to CRM Hub',
    icon: Users,
    action: () => { window.location.href = 'https://crm.quotech.ai' },
    keywords: ['hub', 'crm', 'switch', 'clients'],
    category: 'hub',
  },
  {
    id: 'switch-quote',
    label: 'Switch to Quote Hub',
    icon: FileEdit,
    action: () => { window.location.href = 'https://quote.quotech.ai' },
    keywords: ['hub', 'quote', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-qdrive',
    label: 'Switch to QDrive',
    icon: FolderSearch,
    action: () => { window.location.href = 'https://qdrive.quotech.ai' },
    keywords: ['hub', 'qdrive', 'files', 'switch'],
    category: 'hub',
  },
  {
    id: 'switch-email',
    label: 'Switch to Email Hub',
    icon: Mail,
    action: () => { window.location.href = 'https://email.quotech.ai' },
    keywords: ['hub', 'email', 'switch'],
    category: 'hub',
  },
]

// =============================================================================
// HUB LINKS (for Command Palette)
// =============================================================================

export const HUB_LINKS: ExternalLink[] = EXTERNAL_LINKS

// =============================================================================
// Legacy exports for backwards compatibility
// =============================================================================

export const navigationItems = NAV_ITEMS
export const SECTION_LABELS = sidebarSectionLabels
